import {Container, Point, Sprite, Text} from 'pixi.js';
import {ITrackEntry} from 'pixi-spine';
import {
    Button,
    CoreDI,
    FullScreenFader,
    LanguageService,
    ModuleIsEnabledFilter,
    ObjectBuilder,
    RichText, ScreenParameters, setLayer,
    inject, SpineButton, Spine, SpineButtonState
} from '@gongxigames/framework/core';
import {LAYERS} from '@gongxigames/framework/slot/configs/Layers';
import {DI} from '@gongxigames/framework/slot/configs/DI';
import {GameConfig, IReelsScaleService} from '@gongxigames/framework/slot/types';
import {BuyFeaturePopupEvents} from '@gongxigames/framework/slot/modules/buyFeature/BuyFeatureModule';
import {isEnabledBuyFeature} from '@gongxigames/framework/slot/modules/buyFeature/isEnabledBuyFeature';
import {View} from '@gongxigames/framework/slot/fragments/module/view';
import {Node} from 'posthtml-parser';
import {Line, LineItemType} from '@gongxigames/framework/core/fragments/display/richText/Line';

export type BuyFeatureButtonsTypeToDisable = 'show' | 'confirm';

const priceOffset = 303;
const defaultDesktop = {x: 204, y: 1336};
const defaultLandscape = {x: 234, y: 1336};
const defaultPortrait = {x: 375, y: 3100};
const duration = 500;

export class BuyFeatureView extends View<BuyFeaturePopupEvents> {
    public static readonly NAME: symbol = Symbol('BuyFeatureView');
    public static layer = LAYERS.OVER_KEYPAD;
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledBuyFeature;

    protected buttonContainer = new Container();
    protected buttonShow!: Button;
    protected buttonConfirm!: SpineButton;
    protected buttonCancel!: SpineButton;
    protected buttonsSpine!: SpineButton[];
    protected featurePrice!: RichText;
    protected featureText!: RichText;
    protected fader!: FullScreenFader;
    protected spineContainer = new Container();
    protected spine!: Spine;
    protected spineName = 'popup_buy_feature';
    protected spineButtonsName = 'buttions_popup_buy_a_feature';

    @inject(CoreDI.LanguageService)
    protected languageService!: LanguageService;

    @inject(CoreDI.GameConfig)
    protected gameConfig!: GameConfig;

    @inject(DI.ReelsScaleService)
    protected _reelsScaleService!: IReelsScaleService;

    protected featureName!: string;

    public init(): void {
        super.init();

        this.getElements();
        this.addElements();
        this.setLayerForElements();
        this.setupButtonsEvents();
        this.positionButton();
    }

    protected getElements(): void {
        const buyFeature = this._objectBuilder.buildSprite(ObjectBuilder.resources.shared_sprite.buyFeature);

        this.featurePrice = buyFeature.getChildByName<RichText>('buyFeaturePrice');
        this.fader = this._displayObjectUtils.createFullScreenFader({alpha: 0});

        if (this.gameConfig.buyFeatureConfig?.featureNameTranslationKey) {
            this.featureName = this.languageService.get(this.gameConfig.buyFeatureConfig?.featureNameTranslationKey);
        }

        this.createSpine();

        this.buttonShow = buyFeature.getChildByName<Button>('buyFeatureButton');
        this.buttonContainer.addChild(this.buttonShow, this.featurePrice);
    }

    protected createSpine(): void {
        const slotScale = new Point(0.5, -0.5);

        this.spine = this._objectBuilder.buildSpine(this.spineName);
        this.spine.position.set(this.gameConfig.mainFrame.width / 2, this.gameConfig.mainFrame.height / 2 - 100);

        this.buttonConfirm = this._objectBuilder.buildSpineButton({
            spineName: this.spineButtonsName,
            animationNames: {
                idle: 'idle_yes',
                hoverStart: '-',
                hoverEnd: '-',
                click: 'press_yes_out'
            },
            clickCallback: this.onConfirmButtonPointerUp.bind(this)
        });
        this.buttonCancel = this._objectBuilder.buildSpineButton({
            spineName: this.spineButtonsName,
            animationNames: {
                idle: 'idle_no',
                hoverStart: '-',
                hoverEnd: '-',
                click: 'press_no_out'
            },
            clickCallback: this.onCancelButtonPointerUp.bind(this)
        });
        this.buttonConfirm.name = 'yes';
        this.buttonCancel.name = 'no';
        this.buttonsSpine = [this.buttonConfirm, this.buttonCancel];

        this._objectBuilder.setUpSlot({
            name: 'yes_text_baf',
            richText: {id: 'buyFeaturePopupYesText', style: 'BuyFeaturePopupButtonStyle'}
        }, this.buttonConfirm.spine, slotScale);

        this._objectBuilder.setUpSlot({
            name: 'no_text_baf',
            richText: {id: 'buyFeaturePopupNoText', style: 'BuyFeaturePopupButtonStyle'}
        }, this.buttonCancel.spine, slotScale);

        this.featureText = this._objectBuilder.setUpSlot({
            name: 'feature_name',
            richText: {id: 'buyFeaturePopupText', style: 'BuyFeaturePopupTextStyle', maxWidth: 2550}
        }, this.spine, slotScale) as RichText;

        const spriteYes = this.spine.getChildByName<Sprite>('buttion_yes_art_baf');
        const spriteNo = this.spine.getChildByName<Sprite>('buttion_no_art_baf');

        spriteYes.removeChildren();
        spriteNo.removeChildren();

        this.buttonConfirm.scale.set(0.5, -0.5);
        this.buttonCancel.scale.set(0.5, -0.5);

        spriteYes.addChild(this.buttonConfirm);
        spriteNo.addChild(this.buttonCancel);
    }

    protected setLayerForElements(): void {
        setLayer(LAYERS.KEYPAD, this.buttonContainer);
    }

    protected addElements(): void {
        this.container.addChild(this.buttonContainer, this.fader, this.spineContainer);
    }

    protected positionButton(): void {
        let position = defaultDesktop;
        let positionType: 'desktop' | 'landscape' | 'portrait' = 'desktop';

        if (this.isPlatformMobile()) {
            position = this._screenManager.isLandscapeOrientation() ? defaultLandscape : defaultPortrait;
            positionType = this._screenManager.isLandscapeOrientation() ? 'landscape' : 'portrait';
        }

        const configPosition = this.gameConfig.buyFeatureConfig?.button?.[positionType]?.position;
        const x = configPosition?.x ?? position.x;
        const y = configPosition?.y ?? position.y;

        this.setShowButtonPosition(x , y);
    }

    protected setShowButtonPosition(x: number, y: number): void {
        this.buttonShow.position.set(x, y);
        this.featurePrice.position.set(x, y + priceOffset);
    }

    protected setupButtonsEvents(): void {
        this.buttonShow.callback = this.onShowPopupButtonClicked.bind(this);

        this.buttonConfirm.on('pointerout', this.onPointerOut.bind(this));
        this.buttonCancel.on('pointerout', this.onPointerOut.bind(this));

        this.buttonConfirm.on('pointerdown', this.onPointerDown.bind(this));
        this.buttonCancel.on('pointerdown', this.onPointerDown.bind(this));
    }

    private onPointerDown(e: any): void {
        e.currentTarget.spine.setAnimation({
            animationName: `press_${e.currentTarget.name}_in`
        });
    }

    private get isDisableYes(): boolean {
        return this.buttonConfirm.state === SpineButtonState.DISABLED;
    }

    private onPointerOut(): void {
        if (this.buttonsSpine.filter((b) => b.state === SpineButtonState.DOWN).length) {
            if (this.isDisableYes) {
                this.buttonCancel.idle(true);
            } else {
                this.buttonsSpine.forEach((b) => b.idle(true));
            }
        }
    }

    public onCancelButtonPointerUp(): void {
        this.hide();
        this.emit('BuyFeature.Popup.No');
        this.soundPlayer.play('buttonclick');
        this.clickedOnSpineButtons('outro_no');
    }

    private onConfirmButtonPointerUp(): void {
        const animation = this.spine.spineData.animations.filter((a) => a.name === 'outro_yes')[0];
        const animationDuration = animation?.duration ?? 1.4;
        const delayDuration = Math.round(1000 * animationDuration / 2);

        this.hide();
        this.updateManager.addTimer(() => this.emit('BuyFeature.Popup.Yes'), delayDuration, true);
        this.soundPlayer.play('buttonclick');
        this.clickedOnSpineButtons('outro_yes');
    }

    private clickedOnSpineButtons(animationName = 'outro_yes'): void {
        this.spine.state.data.defaultMix = 0.3;
        this.spine.setAnimation({
            animationName,
            listeners: {
                complete: () => {
                    this.spine.stop();
                    this.spineContainer.removeChildren();
                }
            }
        });
    }

    private clearSpine(spine: Spine): void {
        spine.state.clearListeners();
        spine.state.clearTracks();
        spine.skeleton.setToSetupPose();
    }

    private resetSpines(): void {
        this.clearSpine(this.spine);
        this.spine.visible = true;
        this.spineContainer.addChild(this.spine);
        this.updateManager.add(this.spine);
        this.spine.start();

        this.buttonsSpine.forEach((b) => this.startSpineButton(b));
    }

    private startSpineButton(button: SpineButton): void {
        this.clearSpine(button.spine);
        this.updateManager.add(button.spine);
        button.visible = true;
        button.spine.start();
        button.disable();
    }

    protected onShowPopupButtonClicked(): void {
        this.resetSpines();

        this.enableSpineButtons();
        this.emit('BuyFeature.Popup.Open');
        this.addFaderTween();
        this.soundPlayer.play('buttonclick');

        this.spine.state.data.defaultMix = 0;

        const animationName = this._screenManager.isPortraitOrientation() ? 'intro_portrait' : 'intro_landscape';

        this.spine.setAnimation({
            animationName,
            listeners: {
                complete: () => {
                    this.spine.setAnimation({animationName: 'idle', loop: true});
                }
            }
        });
    }

    public hide(): void {
        this.emit('BuyFeature.Popup.Close');

        this.addFaderTween(true, 0);
        const isDisabled = this.isDisableYes;

        this.buttonsSpine.forEach((b) => {
            const spine = b.spine;
            const animationName = isDisabled ? `disable_${b.name}_out` : `press_${b.name}_out`;

            spine.state.data.defaultMix = 0.3;
            b.disable();
            spine.setAnimation({
                animationName,
                listeners: {
                    complete: (entry: ITrackEntry) => {
                        if (entry.animation.name === animationName) {
                            b.visible = false;
                            spine.stop();
                        }
                    }
                }
            });
        });
    }

    public updatePrice(featurePrice: string): void {
        this.featurePrice.updateText({customText: featurePrice});
        this.patchFeatureTextApplyTagsOnce(featurePrice);
        this.featureText.updateText({values: {featureName: this.featureName, featurePrice}});
    }

    public disableButton(type: BuyFeatureButtonsTypeToDisable): void {
        if (type === 'show') {
            this.buttonShow.disable();
            this.setShowButtonTextAlpha(0.5);
        } else {
            this.buttonConfirm.disable();
            this.buttonConfirm.spine.setAnimation({animationName: 'disable_yes_in'});
        }
    }

    public enableButton(type: BuyFeatureButtonsTypeToDisable): void {
        if (type === 'show') {
            this.buttonShow.enable();
            this.setShowButtonTextAlpha(1);
        } else {
            this.enableSpineButtons();
        }
    }

    private enableSpineButtons(): void {
        this.buttonsSpine.forEach((b) => {
            b.enable();
            b.idle(true);
        });
    }

    private setShowButtonTextAlpha(alpha: number): void {
        this.buttonShow.getChildByName<RichText>('showButtonText', true).alpha = this.featurePrice.alpha = alpha;
    }

    public toggleShowButton(visible: boolean): void {
        this.updateManager.addTween(this.buttonContainer).to({alpha: Number(visible)}, duration).start();
        this.buttonContainer.visible = visible;
    }

    protected addFaderTween(hide = false, alpha = 0.5): void {
        if (!hide) {
            this.fader.interactive = true;
        }

        this.updateManager.addTween(this.fader)
            .to({alpha}, duration)
            .onComplete(() => {
                if (hide) {
                    this.fader.interactive = false;
                }
            })
            .start();
    }

    public onResized(screenParams: ScreenParameters): void {
        this.fader?.resize(screenParams);
    }

    public onOrientationChanged(): void {
        super.onOrientationChanged();

        this._reelsScaleService.scaleByCurrentScreenOrientation(this.spineContainer);
        this.positionButton();
    }

    private patchFeatureTextApplyTagsOnce(featurePrice: string): void {
        const regEx = new RegExp(featurePrice, 'ig');
        // @ts-expect-error - applyTags is private
        const featureTextApplyTags = this.featureText.applyTags;

        // @ts-expect-error - applyTags is private
        this.featureText.applyTags = (context: CursorContext, nodeTree: Node[]) => {
            featureTextApplyTags.call(this.featureText, context, nodeTree);
            const lines = context._lines;

            lines.forEach((line: Line) => {
                const {elements} = line;

                elements.forEach((element) => {
                    const {item} = element as {item: LineItemType;};

                    if (!(item instanceof Text)) {
                        return;
                    }
                    const {text} = item;

                    item.text = text.replace(regEx, featurePrice);
                });
            });
            // @ts-expect-error - applyTags is private
            this.featureText.applyTags = featureTextApplyTags;
        };
    }
}
